import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import BalloonPopGame from '../components/BalloonPopGame';
import { saveUserEntry, getSettings } from '../utils/db';
import { saveBalloonEntry } from '../utils/quizData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ConfettiEffect from '../components/ConfettiEffect';
import { useGameExitRoute } from '../utils/navigation';

const { 
  FiUser, FiPhone, FiCopy, FiBookOpen, FiAward, FiShare2, 
  FiArrowLeft, FiSun, FiMoon 
} = FiIcons;

const BalloonPopFlow = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ name: '', studentClass: '', mobile: '', schoolName: '' });
  const [reward, setReward] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(getSettings());

  // Day vs Night Mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark') || localStorage.getItem('theme') === 'dark';
  });

  const toggleDayNightMode = () => {
    const nextDark = !isDarkMode;
    setIsDarkMode(nextDark);
    if (nextDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  useEffect(() => {
    const handleSettings = () => setSettings(getSettings());
    window.addEventListener('settingsUpdated', handleSettings);
    return () => window.removeEventListener('settingsUpdated', handleSettings);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const generateCoupon = () => {
    return `KIDS-${new Date().getFullYear()}-${Math.floor(100000 + Math.random() * 900000)}`;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return setError('Please enter Student Name to start.');
    }

    // Phone number is NOT mandatory for students/kids games!
    if (formData.mobile.trim()) {
      const phoneClean = formData.mobile.replace(/[^0-9+]/g, '');
      const phoneRegex = /^(?:\+?\d{1,4})?[6-9]\d{9}$/;
      if (!phoneRegex.test(phoneClean)) {
        return setError('Please enter a valid 10-digit mobile number, or leave it blank.');
      }
    }

    setIsSubmitting(true);
    setTimeout(() => {
      setCouponCode(generateCoupon());
      setStep(2);
      setIsSubmitting(false);
    }, 400);
  };

  const handleBalloonPopped = (wonItem) => {
    setReward(wonItem);
    const entryData = {
      name: formData.name.trim(),
      mobile: formData.mobile.trim() || 'N/A',
      receiptNumber: formData.studentClass.trim() || 'Student',
      couponCode,
      reward: wonItem,
      gameType: 'Balloon Pop',
      schoolName: formData.schoolName.trim() || 'Event Stall'
    };

    saveUserEntry(entryData);
    saveBalloonEntry(entryData);
    setStep(3);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(couponCode);
    alert('Coupon code copied! Show this at the prize counter to claim your gift.');
  };

  const resetFlow = () => {
    setStep(1);
    setFormData({ name: '', studentClass: '', mobile: '', schoolName: '' });
    setReward('');
    setCouponCode('');
    setError('');
  };

  return (
    <div 
      className={`min-h-screen relative flex flex-col items-center justify-between p-4 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-slate-950 text-white' 
          : 'bg-gradient-to-br from-sky-50 via-rose-50/50 to-amber-50 text-slate-900'
      }`}
    >
      {/* Background Soft Orbs */}
      <div className={`absolute top-0 left-1/4 w-80 h-80 rounded-full blur-[90px] pointer-events-none ${
        isDarkMode ? 'bg-indigo-600/10' : 'bg-pink-200/50'
      }`}></div>
      <div className={`absolute bottom-0 right-1/4 w-80 h-80 rounded-full blur-[90px] pointer-events-none ${
        isDarkMode ? 'bg-amber-500/10' : 'bg-sky-200/50'
      }`}></div>

      {step === 3 && <ConfettiEffect />}

      {/* TOP HEADER: Back & Day/Night Mode Switch */}
      <header className="relative z-20 w-full max-w-xl mx-auto flex items-center justify-between pt-2">
        <Link 
          to={exitRoute} 
          title={exitTitle}
          className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-colors shadow-xs ${
            isDarkMode 
              ? 'bg-slate-900 border-slate-700 text-slate-300 hover:text-white' 
              : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
          }`}
        >
          <SafeIcon icon={FiArrowLeft} />
        </Link>

        {/* Day / Night Mode Toggle */}
        <button
          type="button"
          onClick={toggleDayNightMode}
          className={`px-3 py-1.5 rounded-xl border text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer ${
            isDarkMode 
              ? 'bg-slate-900 border-slate-800 text-yellow-400 hover:bg-slate-800' 
              : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'
          }`}
        >
          <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="text-sm" />
          <span>{isDarkMode ? 'Day ☀️' : 'Night 🌙'}</span>
        </button>
      </header>

      <div className="relative z-10 w-full max-w-lg my-auto py-4">
        
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm transition-all duration-300 shadow-sm ${
            step >= 1 
              ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-950 font-black' 
              : (isDarkMode ? 'bg-slate-800 text-slate-500' : 'bg-slate-200 text-slate-500')
          }`}>
            1
          </div>
          <div className={`w-12 h-1 rounded-full transition-all duration-300 ${
            step >= 2 ? 'bg-amber-400' : (isDarkMode ? 'bg-slate-800' : 'bg-slate-200')
          }`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm transition-all duration-300 shadow-sm ${
            step >= 2 
              ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-950 font-black' 
              : (isDarkMode ? 'bg-slate-800 text-slate-500' : 'bg-slate-200 text-slate-500')
          }`}>
            2
          </div>
          <div className={`w-12 h-1 rounded-full transition-all duration-300 ${
            step >= 3 ? 'bg-amber-400' : (isDarkMode ? 'bg-slate-800' : 'bg-slate-200')
          }`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm transition-all duration-300 shadow-sm ${
            step >= 3 
              ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-950 font-black' 
              : (isDarkMode ? 'bg-slate-800 text-slate-500' : 'bg-slate-200 text-slate-500')
          }`}>
            3
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* STEP 1: Registration with High-Contrast Visible Labels */}
          {step === 1 && (
            <motion.div 
              key="step1" 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, scale: 0.95 }} 
              className={`rounded-3xl p-6 sm:p-8 border-2 shadow-2xl relative transition-colors ${
                isDarkMode 
                  ? 'bg-slate-900 border-slate-700' 
                  : 'bg-white border-indigo-100 shadow-[0_20px_50px_rgba(99,102,241,0.08)]'
              }`}
            >
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl text-3xl mb-2.5 border shadow-inner ${
                  isDarkMode 
                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' 
                    : 'bg-pink-100 text-pink-600 border-pink-200'
                }`}>
                  🎈
                </div>
                <h1 className={`text-2xl sm:text-3xl font-black tracking-wide ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Kids Balloon Carnival!
                </h1>
                <p className={`text-xs sm:text-sm mt-1.5 font-medium ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Enter details to pop a lucky balloon and win caps, badges & gifts!
                </p>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Field 1: Student Name */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center justify-between">
                    <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      <SafeIcon icon={FiUser} className="text-amber-500 text-sm" /> Student / Child Name
                    </span>
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 font-extrabold uppercase px-2 py-0.5 rounded bg-amber-400/10 border border-amber-400/30">
                      Required
                    </span>
                  </label>
                  <input 
                    type="text" 
                    name="name" 
                    required 
                    placeholder="e.g. Aarav Sharma" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 rounded-xl font-bold text-sm outline-none transition-all border-2 ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-600 focus:border-amber-400 text-white placeholder-slate-400' 
                        : 'bg-slate-50 border-slate-300 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder-slate-400'
                    }`}
                  />
                </div>

                {/* Field 2 & 3: Class / Grade & Parent Mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1.5 ${
                      isDarkMode ? 'text-white' : 'text-slate-800'
                    }`}>
                      <SafeIcon icon={FiBookOpen} className="text-indigo-500 text-sm" /> Class / Grade
                    </label>
                    <input 
                      type="text" 
                      name="studentClass" 
                      placeholder="e.g. Class 5-B" 
                      value={formData.studentClass} 
                      onChange={handleInputChange} 
                      className={`w-full px-4 py-3 rounded-xl font-bold text-sm outline-none transition-all border-2 ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-600 focus:border-amber-400 text-white placeholder-slate-400' 
                          : 'bg-slate-50 border-slate-300 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center justify-between">
                      <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        <SafeIcon icon={FiPhone} className="text-emerald-500 text-sm" /> Parent Mobile
                      </span>
                      <span className={`text-[10px] font-bold lowercase px-1.5 py-0.5 rounded ${
                        isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                      }`}>
                        optional
                      </span>
                    </label>
                    <input 
                      type="tel" 
                      name="mobile" 
                      placeholder="e.g. 9876543210" 
                      value={formData.mobile} 
                      onChange={handleInputChange} 
                      className={`w-full px-4 py-3 rounded-xl font-bold text-sm outline-none transition-all border-2 ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-600 focus:border-amber-400 text-white placeholder-slate-400' 
                          : 'bg-slate-50 border-slate-300 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder-slate-400'
                      }`}
                    />
                  </div>
                </div>

                {/* Field 4: School / Event Name */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center justify-between">
                    <span className={`flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      <SafeIcon icon={FiAward} className="text-pink-500 text-sm" /> School / Event Name
                    </span>
                    <span className={`text-[10px] font-bold lowercase px-1.5 py-0.5 rounded ${
                      isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                    }`}>
                      optional
                    </span>
                  </label>
                  <input 
                    type="text" 
                    name="schoolName" 
                    placeholder="e.g. Delhi Public School / Carnival Stall" 
                    value={formData.schoolName} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 rounded-xl font-bold text-sm outline-none transition-all border-2 ${
                      isDarkMode 
                        ? 'bg-slate-800 border-slate-600 focus:border-amber-400 text-white placeholder-slate-400' 
                        : 'bg-slate-50 border-slate-300 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder-slate-400'
                    }`}
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-600 dark:text-red-200 text-xs text-center font-bold">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-black text-base shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 mt-3 cursor-pointer"
                >
                  {isSubmitting ? "Starting Carnival..." : "Start Balloon Game 🎈"}
                </button>
              </form>
            </motion.div>
          )}

          {/* STEP 2: Play Game */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <BalloonPopGame onFinished={handleBalloonPopped} isDarkMode={isDarkMode} />
            </motion.div>
          )}

          {/* STEP 3: Reward Claim */}
          {step === 3 && (
            <motion.div 
              key="step3" 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              className={`rounded-3xl p-6 md:p-8 border-2 text-center shadow-2xl relative transition-colors ${
                isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-amber-300 shadow-xl'
              }`}
            >
              <div className="w-16 h-16 rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/40 flex items-center justify-center mx-auto mb-4 text-3xl shadow-sm">
                🏆
              </div>

              <span className="text-xs uppercase font-extrabold text-amber-500 tracking-wider">
                Claim Your School Prize
              </span>
              <h2 className={`text-2xl md:text-3xl font-black mt-1 mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Awesome Job, {formData.name}!
              </h2>
              <p className={`text-xs md:text-sm mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Show this official digital claim pass at the school event / stall counter.
              </p>

              {/* Award Pass Card */}
              <div className={`p-6 rounded-2xl border mb-6 text-left relative overflow-hidden ${
                isDarkMode ? 'bg-slate-950 border-amber-500/40' : 'bg-amber-50/60 border-amber-300'
              }`}>
                <div className="absolute top-0 right-0 px-3 py-1 bg-amber-500 text-slate-950 text-[10px] font-black uppercase rounded-bl-xl tracking-wider">
                  Verified Winner
                </div>

                <span className={`text-[10px] uppercase font-bold block mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Gift Won
                </span>
                <div className="text-xl md:text-2xl font-black text-amber-600 dark:text-amber-400 mb-4">{reward}</div>

                <div className={`flex items-center justify-between p-3 rounded-xl border ${
                  isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-xs'
                }`}>
                  <div>
                    <span className={`text-[9px] uppercase font-semibold block ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Claim Coupon Code
                    </span>
                    <span className={`text-base font-mono font-black tracking-wider ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {couponCode}
                    </span>
                  </div>
                  <button 
                    type="button" 
                    onClick={copyToClipboard}
                    className="p-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition-colors flex items-center gap-1 cursor-pointer"
                    title="Copy Code"
                  >
                    <SafeIcon icon={FiCopy} /> Copy
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`https://wa.me/?text=I%20just%20won%20${encodeURIComponent(reward)}%20at%20the%20School%20Carnival!%20Claim%20Code:%20${couponCode}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-md"
                >
                  <SafeIcon icon={FiShare2} /> Share on WhatsApp
                </a>

                <button 
                  type="button" 
                  onClick={resetFlow}
                  className={`py-3 px-6 rounded-xl border font-bold text-xs transition-colors cursor-pointer ${
                    isDarkMode 
                      ? 'border-slate-700 hover:bg-slate-800 text-slate-300' 
                      : 'border-slate-300 hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  Next Child
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default BalloonPopFlow;
